import React, { useRef, useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import type {
  EventClickArg,
  EventDropArg,
  DateSelectArg,
  EventHoveringArg,
} from "@fullcalendar/core";
import type { EventResizeDoneArg } from "@fullcalendar/interaction";
import type { CalendarEventItem } from "@/types/task";
import {
  CalendarDays,
  Calendar as CalendarIcon,
  Video,
  Phone,
  Mail,
  MessageSquare,
  CheckSquare,
  Globe,
  Clock,
  User,
  Users,
  MapPin,
  Building2,
  Tag,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";

interface CalendarViewProps {
  events: CalendarEventItem[];
  loading?: boolean;
  onEventClick: (event: CalendarEventItem) => void;
  onSelectSlot: (start: Date, end: Date, allDay: boolean) => void;
  onRescheduleEvent: (
    id: string,
    start: Date,
    end?: Date,
    kind?: "TASK" | "EXTERNAL"
  ) => Promise<void>;
  onDatesSet?: (start: Date, end: Date) => void;
}

export function CalendarView({
  events,
  loading,
  onEventClick,
  onSelectSlot,
  onRescheduleEvent,
  onDatesSet,
}: CalendarViewProps) {
  const calendarRef = useRef<FullCalendar | null>(null);

  // Map CalendarEventItem to FullCalendar event format
  const fcEvents = useMemo(() => {
    return events.map((item) => {
      let bg = "#0284c7"; // primary blue
      let border = "#0369a1";
      let text = "#ffffff";

      if (item.kind === "EXTERNAL") {
        if (item.provider === "GOOGLE") {
          bg = "#ea4335"; // Google red
          border = "#c5221f";
        } else {
          bg = "#0078d4"; // Outlook blue
          border = "#106ebe";
        }
      } else if (item.task_status === "DONE") {
        bg = "#10b981"; // Emerald
        border = "#059669";
      } else {
        switch (item.task_type) {
          case "MEETING":
            bg = "#2563eb"; // Blue
            border = "#1d4ed8";
            break;
          case "CALL":
            bg = "#d97706"; // Amber
            border = "#b45309";
            break;
          case "EMAIL":
            bg = "#7c3aed"; // Purple
            border = "#6d28d9";
            break;
          case "WHATSAPP":
            bg = "#059669"; // Green
            border = "#047857";
            break;
          case "FOLLOW_UP":
            bg = "#0891b2"; // Cyan
            border = "#0e7490";
            break;
          default:
            bg = "#475569"; // Slate
            border = "#334155";
            break;
        }
      }

      let start = item.start;
      let end = item.end;
      if (!item.all_day && start) {
        const startTime = new Date(start).getTime();
        const endTime = end ? new Date(end).getTime() : 0;
        const defaultDuration = item.task_type === "MEETING" ? 30 : 15;
        if (!endTime || endTime <= startTime) {
          end = new Date(startTime + defaultDuration * 60 * 1000).toISOString();
        }
      }

      return {
        id: item.id,
        title: item.title,
        start,
        end,
        allDay: item.all_day,
        backgroundColor: bg,
        borderColor: border,
        textColor: text,
        editable: true,
        startEditable: true,
        durationEditable: true,
        extendedProps: { rawItem: item },
      };
    });
  }, [events]);

  const [hoveredEvent, setHoveredEvent] = useState<{
    item: CalendarEventItem;
    rect: DOMRect;
  } | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const handleEventMouseEnter = (arg: EventHoveringArg) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    const raw = arg.event.extendedProps.rawItem as CalendarEventItem;
    if (!raw) return;
    const rect = arg.el.getBoundingClientRect();
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredEvent({ item: raw, rect });
    }, 280);
  };

  const handleEventMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredEvent(null);
  };

  const handleEventClick = (arg: EventClickArg) => {
    arg.jsEvent.preventDefault();
    setHoveredEvent(null);
    const raw = arg.event.extendedProps.rawItem as CalendarEventItem;
    if (raw) onEventClick(raw);
  };

  const handleDateSelect = (arg: DateSelectArg) => {
    onSelectSlot(arg.start, arg.end, arg.allDay);
  };

  const handleEventDrop = async (arg: EventDropArg) => {
    setHoveredEvent(null);
    const raw = arg.event.extendedProps.rawItem as CalendarEventItem;
    if (!raw) {
      arg.revert();
      return;
    }
    try {
      let newEnd = arg.event.end;
      if (!newEnd && arg.event.start && raw.start && raw.end) {
        const originalDuration =
          new Date(raw.end).getTime() - new Date(raw.start).getTime();
        newEnd = new Date(
          arg.event.start.getTime() +
            (originalDuration > 0 ? originalDuration : 30 * 60 * 1000)
        );
      }
      await onRescheduleEvent(
        raw.id,
        arg.event.start!,
        newEnd || undefined,
        raw.kind,
      );
    } catch {
      arg.revert();
    }
  };

  const handleEventResize = async (arg: EventResizeDoneArg) => {
    setHoveredEvent(null);
    const raw = arg.event.extendedProps.rawItem as CalendarEventItem;
    if (!raw) {
      arg.revert();
      return;
    }
    try {
      await onRescheduleEvent(
        raw.id,
        arg.event.start!,
        arg.event.end || undefined,
        raw.kind,
      );
    } catch {
      arg.revert();
    }
  };

  const renderEventContent = (eventInfo: any) => {
    const raw = eventInfo.event.extendedProps.rawItem as CalendarEventItem;
    if (!raw) return <span>{eventInfo.event.title}</span>;

    const isTimeGrid = eventInfo.view.type.startsWith("timeGrid");
    const isDayView = eventInfo.view.type === "timeGridDay";

    const renderIcon = (size = "size-3") => {
      if (raw.kind === "EXTERNAL") return <Globe className={`${size} shrink-0`} />;
      switch (raw.task_type) {
        case "MEETING":
          return <Video className={`${size} shrink-0`} />;
        case "CALL":
          return <Phone className={`${size} shrink-0`} />;
        case "EMAIL":
          return <Mail className={`${size} shrink-0`} />;
        case "WHATSAPP":
          return <MessageSquare className={`${size} shrink-0`} />;
        default:
          return <CheckSquare className={`${size} shrink-0`} />;
      }
    };

    const startTimeStr = !raw.all_day && raw.start
      ? new Date(raw.start).toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })
      : "";

    const endTimeStr = !raw.all_day && raw.end
      ? new Date(raw.end).toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })
      : "";

    const fullTimeStr =
      startTimeStr && endTimeStr ? `${startTimeStr} – ${endTimeStr}` : startTimeStr;

    // Day View: Full width executive agenda card
    if (isDayView) {
      return (
        <div
          className="flex items-center justify-between h-full w-full px-2.5 py-1 text-white select-none overflow-hidden pointer-events-none"
          title={`${raw.title}${startTimeStr ? ` (${startTimeStr})` : ""}${raw.related_name ? ` • ${raw.related_name}` : ""}`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="p-1 rounded-md bg-black/20 shrink-0">
              {renderIcon("size-3.5")}
            </div>
            <div className="min-w-0 flex flex-col justify-center">
              <div className="flex items-center gap-2">
                <span className="font-bold text-xs tracking-tight">
                  {fullTimeStr || startTimeStr}
                </span>
                <span className="text-white/50 text-xs">•</span>
                <span className="font-semibold text-xs truncate">
                  {raw.title}
                </span>
                {raw.priority === "HIGH" && (
                  <span className="px-1.5 py-0.2 text-[9px] font-bold rounded bg-red-500/80 text-white uppercase tracking-wider shrink-0">
                    High
                  </span>
                )}
                {raw.task_status === "DONE" && (
                  <span className="px-1.5 py-0.2 text-[9px] font-bold rounded bg-emerald-500/80 text-white uppercase tracking-wider shrink-0">
                    Done
                  </span>
                )}
              </div>
              {raw.related_name && (
                <div className="text-[10px] opacity-85 truncate mt-0.5 font-normal flex items-center gap-1.5">
                  <Building2 className="size-3 shrink-0 opacity-70" />
                  <span>{raw.related_name}</span>
                  {raw.location && (
                    <>
                      <span className="opacity-50">•</span>
                      <MapPin className="size-3 shrink-0 opacity-70" />
                      <span>{raw.location}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          {raw.attendees && raw.attendees.length > 0 && (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-black/20 text-[11px] font-medium shrink-0 ml-2">
              <Users className="size-3 opacity-80" />
              <span>{raw.attendees.length}</span>
            </div>
          )}
        </div>
      );
    }

    // TimeGrid Week View: Smart adaptive card layout
    if (isTimeGrid) {
      const startTimeNum = !raw.all_day && raw.start ? new Date(raw.start).getTime() : 0;
      const endTimeNum = !raw.all_day && raw.end ? new Date(raw.end).getTime() : 0;
      const durationMins =
        startTimeNum && endTimeNum
          ? Math.max(1, Math.round((endTimeNum - startTimeNum) / 60000))
          : 15;
      const isVeryShort = durationMins <= 20;

      // Ultra-short events (<= 20 mins): single horizontal row
      if (isVeryShort) {
        return (
          <div
            className="flex items-center gap-1.5 h-full w-full px-1.5 py-0.5 text-white select-none overflow-hidden text-left pointer-events-none"
            title={`${raw.title}${startTimeStr ? ` (${startTimeStr})` : ""}${raw.related_name ? ` • ${raw.related_name}` : ""}`}
          >
            {renderIcon("size-2.5 shrink-0 opacity-90")}
            <span className="font-bold text-[10px] shrink-0 opacity-95">
              {startTimeStr}
            </span>
            <span className="font-semibold text-[11px] truncate tracking-tight">
              {raw.title}
            </span>
          </div>
        );
      }

      // Standard / Longer events: top time row + title + linked account
      return (
        <div
          className="flex flex-col justify-start h-full w-full px-1.5 py-1 text-white select-none overflow-hidden text-left pointer-events-none"
          title={`${raw.title}${startTimeStr ? ` (${startTimeStr})` : ""}${raw.related_name ? ` • ${raw.related_name}` : ""}`}
        >
          {/* Top Row: Time + Icon */}
          <div className="flex items-center gap-1 font-semibold text-[10px] leading-tight shrink-0 opacity-95">
            {renderIcon("size-2.5 shrink-0 opacity-90")}
            <span className="truncate">{startTimeStr || eventInfo.timeText}</span>
            {raw.attendees && raw.attendees.length > 0 && (
              <span className="ml-auto text-[8px] font-bold px-1 rounded-full bg-black/40 text-white shrink-0">
                {raw.attendees.length}
              </span>
            )}
          </div>

          {/* Event Title */}
          <div className="font-semibold text-[11px] leading-snug truncate mt-0.5">
            {raw.title}
          </div>

          {/* Linked Record (Deal, Account, Lead) */}
          {raw.related_name && (
            <div className="text-[9.5px] opacity-85 truncate leading-tight mt-0.5 font-normal">
              {raw.related_name}
            </div>
          )}
        </div>
      );
    }

    // Month / All-day view (compact horizontal bar)
    return (
      <div
        className="flex items-center gap-1.5 px-2 py-1 w-full overflow-hidden leading-tight text-white select-none cursor-pointer"
        title={`${raw.title}${startTimeStr ? ` (${startTimeStr})` : ""}${raw.related_name ? ` • ${raw.related_name}` : ""}`}
      >
        {renderIcon("size-3")}
        {startTimeStr && (
          <span className="text-[10px] font-semibold opacity-90 shrink-0">
            {startTimeStr}
          </span>
        )}
        <span className="font-medium truncate text-[11px] tracking-tight">
          {raw.title}
        </span>
        {raw.related_name && (
          <span className="text-[10px] opacity-80 truncate max-w-[85px] shrink-0 font-normal hidden sm:inline">
            • {raw.related_name}
          </span>
        )}
        {raw.attendees && raw.attendees.length > 0 && (
          <span className="ml-auto text-[9px] font-bold px-1.5 py-0.2 rounded-full bg-black/30 text-white shrink-0">
            {raw.attendees.length}
          </span>
        )}
      </div>
    );
  };

  // Floating Rich Hover Details Card
  const renderHoverTooltip = () => {
    if (!hoveredEvent || typeof document === "undefined") return null;

    const { item, rect } = hoveredEvent;
    const cardWidth = 340;
    const estimatedHeight = 280;
    const padding = 16;

    // Smart horizontal placement:
    // If the hovered element is on the right half of the screen, place card to the LEFT
    // If on the left half, place card to the RIGHT
    const isRightHalf = rect.left > window.innerWidth / 2;
    let left: number;

    if (isRightHalf) {
      left = rect.left - cardWidth - 12;
      if (left < padding) {
        left = rect.right + 12;
      }
    } else {
      left = rect.right + 12;
      if (left + cardWidth > window.innerWidth - padding) {
        left = rect.left - cardWidth - 12;
      }
    }

    // Clamp horizontally within screen bounds
    left = Math.max(padding, Math.min(left, window.innerWidth - cardWidth - padding));

    // Vertical placement: center vertically relative to event
    const eventCenterY = rect.top + rect.height / 2;
    let top = eventCenterY - estimatedHeight / 2;

    // Clamp vertically within screen bounds
    if (top + estimatedHeight > window.innerHeight - padding) {
      top = window.innerHeight - estimatedHeight - padding;
    }
    if (top < padding) {
      top = padding;
    }

    const start = new Date(item.start);
    const end = new Date(item.end);
    const formattedDate = start.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const formattedTime = item.all_day
      ? "All Day"
      : `${start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} – ${end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;

    const renderTypeIcon = () => {
      if (item.kind === "EXTERNAL") return <Globe className="size-3.5 text-blue-400 shrink-0" />;
      switch (item.task_type) {
        case "MEETING": return <Video className="size-3.5 text-blue-500 shrink-0" />;
        case "CALL": return <Phone className="size-3.5 text-amber-500 shrink-0" />;
        case "EMAIL": return <Mail className="size-3.5 text-purple-500 shrink-0" />;
        case "WHATSAPP": return <MessageSquare className="size-3.5 text-emerald-500 shrink-0" />;
        default: return <CheckSquare className="size-3.5 text-slate-500 shrink-0" />;
      }
    };

    const typeLabel = item.kind === "EXTERNAL"
      ? (item.provider === "GOOGLE" ? "Google Calendar" : "Outlook Calendar")
      : (item.task_type ? item.task_type.replace("_", " ") : "Task");

    return createPortal(
      <div
        style={{
          position: "fixed",
          left,
          top,
          zIndex: 99999,
          width: cardWidth,
        }}
        className="pointer-events-none bg-card/95 backdrop-blur-md border border-border/80 shadow-2xl rounded-xl p-3.5 text-foreground animate-in fade-in-0 zoom-in-95 duration-150"
      >
        {/* Header: Category & Badges */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted text-[11px] font-semibold text-foreground">
            {renderTypeIcon()}
            <span className="capitalize">{typeLabel.toLowerCase()}</span>
          </div>

          <div className="flex items-center gap-1.5">
            {item.priority === "HIGH" && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-500/15 text-rose-600 dark:text-rose-400">
                HIGH
              </span>
            )}
            {item.task_status === "DONE" ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="size-2.5" /> Done
              </span>
            ) : item.task_status === "CANCELLED" ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-500/15 text-rose-600 dark:text-rose-400">
                Cancelled
              </span>
            ) : (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-500/15 text-blue-600 dark:text-blue-400">
                Scheduled
              </span>
            )}
          </div>
        </div>

        {/* Full Event Title */}
        <h4 className="font-bold text-sm text-foreground mt-2 leading-snug break-words">
          {item.title}
        </h4>

        {/* Date & Time */}
        <div className="flex items-center gap-1.5 text-muted-foreground mt-1.5 text-[11px]">
          <Clock className="size-3.5 text-primary shrink-0" />
          <span className="font-medium text-foreground">{formattedDate}</span>
          <span>•</span>
          <span>{formattedTime}</span>
        </div>

        {/* Associated CRM Record (Deal, Account, Lead) */}
        {item.related_name && (
          <div className="flex items-center gap-1.5 text-[11px] text-foreground font-medium mt-2 bg-muted/60 px-2.5 py-1.5 rounded-lg border border-border/50">
            <Building2 className="size-3.5 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground text-[10px] uppercase font-bold">
              {item.related_type || "Linked"}:
            </span>
            <span className="truncate font-semibold">{item.related_name}</span>
          </div>
        )}

        {/* Assignee / Owner */}
        {item.assigned_to && (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-1.5">
            <User className="size-3.5 shrink-0" />
            <span>
              Assigned to:{" "}
              <strong className="text-foreground font-medium">
                {item.assigned_to.first_name} {item.assigned_to.last_name || ""}
              </strong>
            </span>
          </div>
        )}

        {/* Attendees */}
        {item.attendees && item.attendees.length > 0 && (
          <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground mt-1">
            <Users className="size-3.5 mt-0.5 shrink-0" />
            <span className="truncate">
              {item.attendees.length} Attendee{item.attendees.length > 1 ? "s" : ""}:{" "}
              <span className="text-foreground font-medium">
                {item.attendees.map((a) => a.name || a.email).slice(0, 3).join(", ")}
                {item.attendees.length > 3 ? "..." : ""}
              </span>
            </span>
          </div>
        )}

        {/* Location or Online Video Call */}
        {item.online_link ? (
          <div className="flex items-center gap-1.5 text-[11px] text-primary mt-1 font-medium truncate">
            <Video className="size-3.5 shrink-0" />
            <span className="truncate">Video Meeting link included</span>
          </div>
        ) : item.location ? (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-1 truncate">
            <MapPin className="size-3.5 shrink-0" />
            <span className="truncate">{item.location}</span>
          </div>
        ) : null}

        {/* Description Preview */}
        {item.description && (
          <p className="mt-2 pt-1.5 border-t border-border/60 text-[11px] text-muted-foreground line-clamp-2 leading-relaxed italic">
            "{item.description}"
          </p>
        )}

        {/* Footer Hint */}
        <div className="mt-2.5 pt-1.5 border-t border-border/40 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>Click to view full details</span>
          <span className="text-primary font-semibold">Open &rarr;</span>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="relative w-full h-full bg-card rounded-2xl border border-border p-4 shadow-xs">
      {loading && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-xs z-10 flex items-center justify-center rounded-2xl">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-card border border-border shadow-md text-xs font-semibold">
            <div className="size-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span>Updating calendar schedule...</span>
          </div>
        </div>
      )}

      <FullCalendar
        ref={calendarRef}
        plugins={[interactionPlugin, timeGridPlugin, dayGridPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        buttonText={{
          today: "Today",
          dayGridMonth: "Month",
          timeGridWeek: "Week",
          timeGridDay: "Day",
        }}
        dayHeaderFormat={{
          weekday: "short",
          day: "numeric",
          omitCommas: true,
        }}
        eventDisplay="block"
        events={fcEvents}
        editable={true}
        droppable={true}
        dragScroll={true}
        eventStartEditable={true}
        eventDurationEditable={true}
        eventResizableFromStart={true}
        eventDragMinDistance={3}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={4}
        eventMinHeight={22}
        defaultTimedEventDuration="00:15:00"
        forceEventDuration={true}
        slotDuration="00:30:00"
        snapDuration="00:15:00"
        dragRevertDuration={150}
        slotLabelInterval="01:00:00"
        slotEventOverlap={true}
        eventMaxStack={4}
        allDaySlot={true}
        stickyHeaderDates={true}
        moreLinkClick="popover"
        weekends={true}
        nowIndicator={true}
        navLinks={true}
        scrollTime="08:00:00"
        eventClick={handleEventClick}
        eventMouseEnter={handleEventMouseEnter}
        eventMouseLeave={handleEventMouseLeave}
        eventDragStart={() => setHoveredEvent(null)}
        eventResizeStart={() => setHoveredEvent(null)}
        select={handleDateSelect}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventContent={renderEventContent}
        datesSet={(info) => onDatesSet?.(info.start, info.end)}
        height="auto"
        slotMinTime="07:00:00"
        slotMaxTime="21:00:00"
        expandRows={true}
      />

      {renderHoverTooltip()}
    </div>
  );
}
